const PALETTE = {
  kill: '#ffcf66',
  ring: '#ffcf66',
  critical: '#ffe36f',
  armor: '#c1afff',
  body: '#8bf4ff',
  squad: '#74ffcf',
};

/** Presentation of settled domain awards; never computes or changes score.
 * Ages are domain seconds, so pause and replay preserve the same toast frame. */
export function skyScoreFeedback(state) {
  const cues = [];
  for (const burst of state.bursts ?? []) {
    if (!['kill', 'ring'].includes(burst.effect) || !(burst.scoreAward > 0)) continue;
    const age = (burst.duration ?? 1.05) - burst.life;
    if (age < 0 || age >= 0.95) continue;
    cues.push({
      id: `${burst.effect}:${burst.id}`,
      x: burst.x,
      y: burst.y,
      z: burst.z,
      age,
      title: `+${Math.round(burst.scoreAward).toLocaleString('en-US')}`,
      subtitle:
        burst.effect === 'ring'
          ? 'GATE CLEAR'
          : burst.combo > 1
            ? `${burst.combo}× CHAIN`
            : 'ELIMINATED',
      kind: burst.effect,
      color: ['#ffcf66', '#81f4ff', '#ffcf66', '#ffa7e8', '#74ffcf'][
        Math.min(4, Math.max(0, (burst.combo ?? 1) - 1))
      ],
    });
  }
  const impact = state.lastImpact,
    hitAge = impact ? state.time - impact.time : Infinity;
  if (
    hitAge >= 0 &&
    hitAge < 0.48 &&
    !cues.some((cue) => Math.hypot(cue.x - impact.x, cue.y - impact.y, cue.z - impact.z) < 2)
  )
    cues.push({
      ...impact,
      id: `hit:${impact.time}`,
      age: hitAge,
      title:
        impact.quality === 'critical'
          ? 'CRITICAL'
          : impact.quality === 'armor'
            ? 'ARMOURED'
            : 'HIT',
      subtitle: impact.scoreAward > 0 ? `+${impact.scoreAward}` : '',
      kind: impact.quality ?? 'body',
    });
  const clear = state.lastSquadClear,
    clearAge = clear ? state.time - clear.time : Infinity;
  if (clear?.score > 0 && clearAge >= 0 && clearAge < 0.95)
    cues.push({
      ...clear,
      id: `squad:${clear.id}`,
      age: clearAge,
      title: `+${Math.round(clear.score).toLocaleString('en-US')}`,
      subtitle: clear.firstPass
        ? clear.powerUp
          ? 'FIRST PASS · POWER UP'
          : 'FIRST PASS'
        : 'WAVE CLEAR',
      kind: 'squad',
    });
  return cues.slice(-8);
}

/** Eight reusable source-anchored sprites. Labels rise locally from the actual
 * contact; crowding may suppress a label but never moves it to an unrelated
 * screen region. Global wave announcements retain their own HUD owner. */
export function createSkyScorePopups(T, scene, own, reduced) {
  const group = new T.Group();
  group.name = 'Sky arcade score feedback';
  scene.add(group);
  const slots = Array.from({ length: 8 }, () => {
    const canvas = globalThis.document?.createElement?.('canvas');
    if (canvas) {
      canvas.width = 512;
      canvas.height = 192;
    }
    const context = canvas?.getContext('2d');
    const texture = context ? own(new T.CanvasTexture(canvas)) : null;
    if (texture) texture.colorSpace = T.SRGBColorSpace;
    const material = own(
      new T.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    const sprite = new T.Sprite(material);
    sprite.visible = false;
    sprite.renderOrder = 14;
    group.add(sprite);
    return { sprite, canvas, context, texture, key: '' };
  });
  const point = new T.Vector3(),
    projected = new T.Vector3();
  function paint(slot, cue) {
    const key = `${cue.kind}/${cue.title}/${cue.subtitle}/${cue.color}`;
    if (slot.key === key) return;
    slot.key = key;
    slot.sprite.userData.feedback = { title: cue.title, subtitle: cue.subtitle, kind: cue.kind };
    const ctx = slot.context;
    if (!ctx) return;
    ctx.clearRect(0, 0, 512, 192);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 12;
    ctx.strokeStyle = '#101a2be6';
    ctx.shadowColor = '#071222';
    ctx.shadowBlur = 14;
    ctx.font = `900 ${cue.title.length > 8 ? 58 : 78}px system-ui, sans-serif`;
    ctx.strokeText(cue.title, 256, 72);
    ctx.fillStyle = cue.color ?? PALETTE[cue.kind] ?? PALETTE.body;
    ctx.fillText(cue.title, 256, 72);
    if (cue.subtitle) {
      ctx.font = '800 29px system-ui, sans-serif';
      ctx.lineWidth = 8;
      ctx.strokeText(cue.subtitle, 256, 132);
      ctx.fillStyle = '#f6fcff';
      ctx.fillText(cue.subtitle, 256, 132);
    }
    slot.texture.needsUpdate = true;
  }
  function draw(state, camera, aim) {
    const cues = skyScoreFeedback(state).sort((a, b) => a.age - b.age),
      placed = [];
    if (camera) {
      camera.updateMatrixWorld();
    }
    if (aim && camera) {
      projected.set(aim.x, aim.y, aim.z).project(camera);
      placed.push({ x: projected.x, y: projected.y, halfWidth: 0.09, halfHeight: 0.09 });
    }
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i],
        cue = cues[i];
      slot.sprite.visible = !!cue && !!camera && !!slot.context;
      if (!cue || !camera) continue;
      point.set(cue.x, cue.y, cue.z);
      projected.copy(point).applyMatrix4(camera.matrixWorldInverse);
      const depth = -projected.z;
      if (depth <= camera.near) {
        slot.sprite.visible = false;
        continue;
      }
      paint(slot, cue);
      projected.copy(point).project(camera);
      // An off-screen reward must not masquerade as an event at the screen edge.
      if (Math.abs(projected.x) > 1 || Math.abs(projected.y) > 1) {
        slot.sprite.visible = false;
        continue;
      }
      const sourceX = projected.x,
        sourceY = projected.y;
      const span = 2 * depth * Math.tan((camera.fov * Math.PI) / 360);
      const height = span * 0.058,
        duration = cue.kind === 'kill' || cue.kind === 'squad' ? 0.95 : 0.48;
      const p = cue.age / duration,
        rise = reduced() ? 0 : (1 - (1 - p) ** 2) * 0.11;
      const pop = reduced() ? 1 : 1 + 0.13 * Math.sin(Math.min(1, cue.age / 0.16) * Math.PI);
      const halfWidth = (((height * 512) / 192) * pop) / (span * camera.aspect),
        halfHeight = (height * pop) / span;
      const edgeX = Math.max(0, 0.96 - halfWidth),
        edgeY = 0.92 - halfHeight;
      const x = Math.max(-edgeX, Math.min(edgeX, sourceX));
      let chosen = false;
      // Stay on the source's horizontal lane, with at most a small local stack.
      // Newer events take priority when several contacts occupy the same space.
      for (const extra of [0, 0.08, 0.16]) {
        const y = Math.min(edgeY, sourceY + halfHeight + rise + extra);
        if (Math.abs(y) > edgeY || Math.abs(y - sourceY) > 0.3) continue;
        if (
          placed.some(
            (other) =>
              Math.abs(other.x - x) < other.halfWidth + halfWidth &&
              Math.abs(other.y - y) < other.halfHeight + halfHeight + 0.008,
          )
        )
          continue;
        projected.x = x;
        projected.y = y;
        chosen = true;
        break;
      }
      if (!chosen) {
        slot.sprite.visible = false;
        continue;
      }
      placed.push({ x: projected.x, y: projected.y, halfWidth, halfHeight });
      point.copy(projected).unproject(camera);
      slot.sprite.position.copy(point);
      slot.sprite.userData.source = { x: cue.x, y: cue.y, z: cue.z };
      slot.sprite.scale.set(((height * 512) / 192) * pop, height * pop, 1);
      slot.sprite.material.opacity = p < 0.55 ? 1 : Math.max(0, (1 - p) / 0.45);
    }
  }
  return {
    draw,
    dispose() {
      scene.remove(group);
    },
  };
}
